class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    // Hide visually but keep layout to prevent height collapse (no CLS)
    oldNode.style.visibility = 'hidden';
    oldNode.style.pointerEvents = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

class QuantityInputDawn extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input-dawn', QuantityInputDawn);

class ProductVariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {

      const previouslySelected = Array.from(target.options)
        .find((option) => option.getAttribute('selected'));
      if (previouslySelected) {
        previouslySelected.removeAttribute('selected');
      }

      // change previous target.selectedOptions with new value
      const newSelectedOption = Array.from(target.options).find((option) => option.value === value);
      if (newSelectedOption) {
        newSelectedOption.setAttribute('selected', 'selected');
      }

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .product-option__swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('product-option__swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('product-option__swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) {
        selectedSwatchValue.innerHTML = value;
        // Also update the data attribute for external apps (like stone marketplace)
        selectedSwatchValue.setAttribute('data-selected-value', value);
      }
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}
customElements.define('product-variant-selects', ProductVariantSelects);

if (!customElements.get('product-info')) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
        this.productMediaGallery = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        this.productVariantSelects = this.querySelector('product-variant-selects');
        this.variantImageMap = this.parseVariantImageMap();
        this.currentVariantImageKey = null;
        // FIX: Separate cache for slider and grid since both exist
        this.originalSliderHTML = null;
        this.originalGridHTML = null;
        this.originalThumbsHTML = null;
        this.galleryLoader = this.productMediaGallery?.querySelector('[data-media-loader]');
        this.galleryBaseHeight = null;
        this.hasInitializedVariantGallery = false;
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );
        this.initUpdateMedia();

        // CRITICAL FIX #1: Cache original gallery state BEFORE any mapping is applied
        this.cacheOriginalGalleryState();
        this.measureInitialGalleryHeight();

        if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
          this.updateVariantMedia();
        }

        const initialVariant = this.getSelectedVariant(this);
        this.applyVariantImageMap(initialVariant);
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(
          ".product-form__quantity"
        );
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      initUpdateMedia() {
        const variantIdFromUrl = parseInt(window.location.search.split('?').filter(item => item.includes('variant='))[0]?.split('=')[1]) || null;
        if (!variantIdFromUrl) { return; }
        const variant = this.getSelectedVariant(this);
        if (!variant || !variantIdFromUrl) { return; }
        if (variant?.id === variantIdFromUrl) {
          this.updateMedia(variant?.featured_media?.id);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push(html =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach(element =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push(newNode => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues }
      }) {
        if (!this.contains(event.target)) return;

        /* Flag to skip gallery reload for Durability Upgrade changes */
        const changedFieldset = event.target.closest('fieldset[data-option-name]');
        this._skipGalleryUpdate = !!(changedFieldset && changedFieldset.getAttribute('data-option-name') === 'Durability Upgrade');

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl)
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return html => {
          this.productModal?.remove();
          // FIX: Reset all gallery caches
          this.originalSliderHTML = null;
          this.originalGridHTML = null;
          this.originalThumbsHTML = null;

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(
            html.querySelector(selector)
          );
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then(response => response.text())
          .then(responseText => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch(error => {
            if (error.name === "AbortError") {
              // console.log("Fetch aborted by user");
            } else {
              // console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "product-variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage &&
          params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const incomingVariantSelects = html.querySelector("product-variant-selects");
        const currentVariantSelects = this.variantSelectors;

        if (!incomingVariantSelects || !currentVariantSelects) {
          return;
        }

        this.syncVariantSelectors(currentVariantSelects, incomingVariantSelects);
        this.productVariantSelects = currentVariantSelects;
      }

      syncVariantSelectors(currentVariantSelects, incomingVariantSelects) {
        this.updateVariantSelectorScripts(currentVariantSelects, incomingVariantSelects);
        this.syncVariantOptionGroups(currentVariantSelects, incomingVariantSelects);

        Array.from(incomingVariantSelects.attributes).forEach(attribute => {
          if (attribute.name.startsWith('data-')) {
            currentVariantSelects.setAttribute(attribute.name, attribute.value);
          }
        });
      }

      updateVariantSelectorScripts(currentVariantSelects, incomingVariantSelects) {
        ['[data-selected-variant]', '[data-all-variants]', '[data-selected-variant-options-handle]'].forEach(selector => {
          const currentScript = currentVariantSelects.querySelector(selector);
          const incomingScript = incomingVariantSelects.querySelector(selector);

          if (currentScript && incomingScript) {
            currentScript.textContent = incomingScript.textContent;
          }
        });
      }

      syncVariantOptionGroups(currentVariantSelects, incomingVariantSelects) {
        const currentGroups = Array.from(currentVariantSelects.querySelectorAll('[data-option-name]'));
        const incomingGroups = Array.from(incomingVariantSelects.querySelectorAll('[data-option-name]'));

        incomingGroups.forEach(incomingGroup => {
          const optionName = incomingGroup.dataset.optionName;
          if (!optionName) return;

          const currentGroup = currentGroups.find(group => group.dataset.optionName === optionName);
          if (!currentGroup) return;

          this.syncOptionGroup(currentGroup, incomingGroup);
        });
      }

      syncOptionGroup(currentGroup, incomingGroup) {
        this.syncOptionHeader(currentGroup, incomingGroup);

        if (incomingGroup.tagName === 'FIELDSET') {
          this.syncRadioOptionGroup(currentGroup, incomingGroup);
        } else {
          this.syncDropdownOptionGroup(currentGroup, incomingGroup);
        }
      }

      syncOptionHeader(currentGroup, incomingGroup) {
        const currentSelectedValue = currentGroup.querySelector('[data-selected-value]');
        const incomingSelectedValue = incomingGroup.querySelector('[data-selected-value]');

        if (currentSelectedValue && incomingSelectedValue) {
          if (incomingSelectedValue.childElementCount > 0) {
            currentSelectedValue.innerHTML = incomingSelectedValue.innerHTML;
          } else {
            currentSelectedValue.textContent = incomingSelectedValue.textContent;
          }

          if (incomingSelectedValue.hasAttribute('data-selected-value')) {
            currentSelectedValue.setAttribute('data-selected-value', incomingSelectedValue.getAttribute('data-selected-value'));
          } else {
            currentSelectedValue.removeAttribute('data-selected-value');
          }
        }

        const currentDetail = currentGroup.querySelector('[data-option-detail]');
        const incomingDetail = incomingGroup.querySelector('[data-option-detail]');

        if (currentDetail && incomingDetail) {
          currentDetail.innerHTML = incomingDetail.innerHTML;
        }
      }

      syncRadioOptionGroup(currentGroup, incomingGroup) {
        const currentInputsByKey = new Map();
        currentGroup.querySelectorAll('.product-option__input').forEach(input => {
          const key = input.dataset.optionValueId || input.value || input.id;
          currentInputsByKey.set(key, input);
        });

        incomingGroup.querySelectorAll('.product-option__input').forEach(incomingInput => {
          const key = incomingInput.dataset.optionValueId || incomingInput.value || incomingInput.id;
          const currentInput = currentInputsByKey.get(key);
          if (!currentInput) return;

          this.syncOptionInput(currentInput, incomingInput);

          const currentLabel = currentGroup.querySelector(`label[for="${currentInput.id}"]`);
          const incomingLabel = incomingGroup.querySelector(`label[for="${incomingInput.id}"]`);
          if (currentLabel && incomingLabel) {
            this.syncOptionLabel(currentLabel, incomingLabel);
          }
        });
      }

      syncDropdownOptionGroup(currentGroup, incomingGroup) {
        const currentSelect = currentGroup.querySelector('select');
        const incomingSelect = incomingGroup.querySelector('select');
        if (currentSelect && incomingSelect) {
          const previousValue = currentSelect.value;
          currentSelect.innerHTML = incomingSelect.innerHTML;
          currentSelect.value = incomingSelect.value;
          this.copyDataAttributes(currentSelect, incomingSelect);

          if (previousValue !== currentSelect.value) {
            currentSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      syncOptionInput(currentInput, incomingInput) {
        const isChecked = incomingInput.checked || incomingInput.hasAttribute('checked');
        const isDisabled = incomingInput.disabled || incomingInput.hasAttribute('disabled');

        currentInput.checked = isChecked;
        currentInput.toggleAttribute('checked', isChecked);
        currentInput.disabled = isDisabled;
        currentInput.toggleAttribute('disabled', isDisabled);
        currentInput.classList.toggle('disabled', incomingInput.classList.contains('disabled'));

        this.copyDataAttributes(currentInput, incomingInput);

        const ariaLabel = incomingInput.getAttribute('aria-label');
        if (ariaLabel) {
          currentInput.setAttribute('aria-label', ariaLabel);
        }
      }

      syncOptionLabel(currentLabel, incomingLabel) {
        currentLabel.className = incomingLabel.className;

        const currentSpans = currentLabel.querySelectorAll('span');
        const incomingSpans = incomingLabel.querySelectorAll('span');

        incomingSpans.forEach((incomingSpan, index) => {
          const currentSpan = currentSpans[index];
          if (!currentSpan) return;

          currentSpan.className = incomingSpan.className;

          if (incomingSpan.hasAttribute('data-selected-value')) {
            currentSpan.setAttribute('data-selected-value', incomingSpan.getAttribute('data-selected-value'));
          } else {
            currentSpan.removeAttribute('data-selected-value');
          }

          if (incomingSpan.childElementCount === 0) {
            currentSpan.textContent = incomingSpan.textContent;
          }
        });
      }

      copyDataAttributes(target, source) {
        Array.from(target.attributes).forEach(attribute => {
          if (attribute.name.startsWith('data-') && !source.hasAttribute(attribute.name)) {
            target.removeAttribute(attribute.name);
          }
        });

        Array.from(source.attributes).forEach(attribute => {
          if (attribute.name.startsWith('data-')) {
            target.setAttribute(attribute.name, attribute.value);
          }
        });
      }

      handleUpdateProductInfo(productUrl) {
        return html => {
          const variant = this.getSelectedVariant(html);
          const mappedEntries = this.getVariantImageEntries(variant);

          // this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateVariantDiscountBadge(variant?.price, variant?.compare_at_price);
          this.updateVariantDetails(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          // CRITICAL FIX: Update price and other info BEFORE any early returns
          const updateSourceFromDestination = (
            id,
            shouldHide = source => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {
              destination.innerHTML = source.innerHTML;

              if (id === 'Inventory') {
                const inventoryThreshold = parseInt(source.dataset.inventoryThreshold);
                const inventoryQuantity = parseInt(source.dataset.inventoryQuantity);
                destination.classList.toggle("hidden", inventoryThreshold && inventoryQuantity > inventoryThreshold || inventoryQuantity < 1);
              } else {
                destination.classList.toggle("hidden", shouldHide(source));
              }
            } else {
              debugLog('Price', `${id} update skipped - source or destination not found`);
            }
          };

          updateSourceFromDestination("ProductPurchaseInfo");
          updateSourceFromDestination("ProductStickyCart");
          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) => classList.contains("hidden"));
          updateSourceFromDestination("Inventory", ({ innerText }) => innerText === "");

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");

          const buttonElement = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const isDisabled = buttonElement?.hasAttribute("disabled") ?? true;

          // DEBUG: Log button state
          debugLog('Variant Change', 'Button disabled state:', isDisabled);
          debugLog('Variant Change', 'Variant:', variant);
          debugLog('Variant Change', 'Button element:', buttonElement);

          this.productForm?.toggleSubmitButton(
            isDisabled,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant
            }
          });

          // Skip gallery updates entirely for Durability Upgrade changes
          if (this._skipGalleryUpdate) {
            this._skipGalleryUpdate = false;
            return;
          }

          // Bands metal-tone galleries are filtered client-side by
          // bands-variant-media.js: every variant shares the same media and only
          // visibility differs. Skip the section fetch/rebuild here -- it is
          // redundant, races the tone filter, throws during swiper re-init, and
          // leaves a stuck min-height on the slider/swiper that accumulates a
          // growing blank gap on each color switch.
          if (this.productMediaGallery?.dataset?.filterVariantTone === 'true') {
            return;
          }

          // FIX #2: Simplified logic to avoid double-rendering
          // Priority 1: If we have a mapping, use it and skip gallery fetch
          if (mappedEntries) {
            this.renderVariantImageSlides(mappedEntries, variant, {
              suppressSpinner: !this.hasInitializedVariantGallery
            });
            // No need to call updateMedia - renderVariantImageSlides handles the display
            return; // Exit early to prevent any other gallery logic
          }

          // Priority 2: No mapping - check if we should fetch full gallery
          const shouldFetchVariantGallery = !!(variant && variant.id);

          if (shouldFetchVariantGallery) {
            const destinationGallery = this.productMediaGallery;
            const measuredHeight = destinationGallery?.getBoundingClientRect().height || 0;
            const spinnerHeight = this.showVariantImageLoadingSpinner(measuredHeight);
            destinationGallery?.classList.add('is-loading');

            fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${this.sectionId}`)
              .then(r => r.text())
              .then(txt => {
                const doc = new DOMParser().parseFromString(txt, 'text/html');
                const sourceGallery = doc.querySelector(`#MediaGallery-${this.sectionId}`);
                if (sourceGallery && destinationGallery) {
                  const postProcess = [
                    ...this.postProcessHtmlCallbacks,
                    newNode => {
                      this.productMediaGallery = newNode;
                      this.refreshGalleryLoader();

                      // Reset cached states since the gallery is replaced
                      this.originalSliderHTML = null;
                      this.originalGridHTML = null;
                      this.originalThumbsHTML = null;

                      const effectiveHeight = spinnerHeight || measuredHeight || this.galleryBaseHeight;
                      if (effectiveHeight) {
                        newNode.style.minHeight = `${effectiveHeight}px`;
                      }

                      const newSwiperEl = newNode.querySelector('.swiper--product-gallery');
                      const newSliderWrapper = newNode.querySelector('.main-product__media--slider-wrapper');
                      if (effectiveHeight) {
                        newSwiperEl?.style.setProperty('min-height', `${effectiveHeight}px`);
                        newSliderWrapper?.style.setProperty('min-height', `${effectiveHeight}px`);
                      }

                      newNode.classList.add('is-loading');

                      // FIX: Don't re-initialize thumbnails to prevent refresh/blink
                      const thumbsEl = newNode.querySelector('swiper-product-thumbs');
                      // Skip: if (thumbsEl && typeof thumbsEl.init === 'function') thumbsEl.init();
                      const galleryEl = newNode.querySelector('swiper-product-gallery');
                      if (galleryEl && typeof galleryEl.init === 'function') galleryEl.init();

                      if (galleryEl?.instance) {
                        try {
                          galleryEl.instance.slideTo(0, 0);
                        } catch (e) {
                          console.warn('Swiper slideTo failed', e);
                        }
                      }

                      this.updateMedia(variant?.featured_media?.id);
                      this.cacheOriginalGalleryState();

                      this.prepareGalleryImages(newNode);

                      this.showVariantImageLoadingSpinner(effectiveHeight);

                      const finalize = (attempt = 0) => {
                        const swiperReady = !galleryEl || (galleryEl.instance && newSwiperEl?.classList.contains('swiper-initialized'));
                        if (swiperReady || attempt >= 40) {
                          newNode.classList.remove('is-loading');
                          newNode.style.removeProperty('min-height');
                          newSwiperEl?.style.removeProperty('min-height');
                          newSliderWrapper?.style.removeProperty('min-height');
                          this.updateGalleryBaseHeight();
                          this.hideVariantImageLoadingSpinner();
                          this.hasInitializedVariantGallery = true;
                          return;
                        }
                        setTimeout(() => finalize(attempt + 1), 50);
                      };

                      this.waitForPrimaryImageLoad(newNode, () => finalize());
                      setTimeout(() => finalize(40), 5000);
                    }
                  ];

                  HTMLUpdateUtility.viewTransition(
                    destinationGallery,
                    sourceGallery,
                    this.preProcessHtmlCallbacks,
                    postProcess
                  );
                } else {
                  destinationGallery?.classList.remove('is-loading');
                  this.hideVariantImageLoadingSpinner();
                }
              })
              .catch((err) => {
                console.error('[Variant Image Map] Gallery fetch failed:', err);
                destinationGallery?.classList.remove('is-loading');
                this.hideVariantImageLoadingSpinner();
                // Fallback to legacy hide/show method
                if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
                  this.updateVariantMedia(html);
                }
              });
          } else if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
            // Fallback to legacy hide/show if no variant ID
            this.updateVariantMedia(html);
          } else {
            // No mapping, no variant, no legacy mode - just update media if possible
            this.updateMedia(variant?.featured_media?.id);
          }
        };
      }

      parseVariantImageMap() {
        const script = this.querySelector('script[data-variant-image-map]');
        if (!script) {
          return null;
        }

        try {
          const parsed = JSON.parse(script.textContent);
          if (!parsed) {
            console.warn('[Variant Image Map] Metafield script is empty');
            return null;
          }

          // FIX #3: Handle both direct objects and metafield wrapper objects
          const data =
            typeof parsed === 'object' && parsed !== null && parsed.value && typeof parsed.value === 'object'
              ? parsed.value
              : parsed;

          if (!data || typeof data !== 'object') {
            console.warn('[Variant Image Map] Invalid data structure:', data);
            return null;
          }

          // FIX #4: Create case-insensitive lookups AND normalized key map
          this.variantImageMapShapeLookup = new Map();
          this.variantImageMapMetalLookup = new Map();
          this.variantImageMapNormalized = new Map(); // New: normalized key -> original key mapping

          Object.keys(data).forEach(originalKey => {
            const parts = originalKey.split('|');
            if (parts.length < 2) {
              return;
            }

            const shape = parts[0]?.trim();
            const metal = parts[1]?.trim();

            if (shape && metal) {
              // Store lowercase -> original case mapping for lookups
              this.variantImageMapShapeLookup.set(shape.toLowerCase(), shape);
              this.variantImageMapMetalLookup.set(metal.toLowerCase(), metal);

              // Store normalized key (lowercase) -> original key
              const normalizedKey = `${shape.toLowerCase()}|${metal.toLowerCase()}`;
              this.variantImageMapNormalized.set(normalizedKey, originalKey);
            }
          });
          return data;
        } catch (error) {
          console.error('[Variant Image Map] Failed to parse metafield JSON:', error);
          return null;
        }
      }

      buildVariantImageMapKey(variant) {
        if (!variant) {
          return null;
        }

        if (!this.variantImageMap) {
          return null;
        }

        // FIX #4: Collect ALL variant option values (case-insensitive)
        const optionValues = [];

        // Method 1: Try option1, option2, option3 properties
        ['option1', 'option2', 'option3'].forEach(key => {
          if (variant[key]) {
            optionValues.push(typeof variant[key] === 'string' ? variant[key].trim() : variant[key]);
          }
        });

        // Method 2: Try options array (fallback)
        if (Array.isArray(variant.options) && variant.options.length) {
          variant.options.forEach(value => {
            if (value) {
              optionValues.push(typeof value === 'string' ? value.trim() : value);
            }
          });
        }

        // Deduplicate and normalize
        const uniqueValues = [...new Set(optionValues)]
          .map(value => (value && value.toString ? value.toString().trim() : ''))
          .filter(Boolean);

        if (!uniqueValues.length) {
          return null;
        }

        // FIX #4: Match shape and metal using CASE-INSENSITIVE lookups
        let shapeValue = null;
        let metalValue = null;

        uniqueValues.forEach(value => {
          const lower = value.toLowerCase();

          // Find shape (if not already found)
          if (!shapeValue && this.variantImageMapShapeLookup?.has(lower)) {
            shapeValue = this.variantImageMapShapeLookup.get(lower); // Returns original case
          }

          // Find metal (if not already found)
          if (!metalValue && this.variantImageMapMetalLookup?.has(lower)) {
            metalValue = this.variantImageMapMetalLookup.get(lower); // Returns original case
          }
        });

        // If we found both shape and metal, build the key
        if (shapeValue && metalValue) {
          // Use normalized key to look up the original key from the map
          const normalizedKey = `${shapeValue.toLowerCase()}|${metalValue.toLowerCase()}`;
          const originalKey = this.variantImageMapNormalized.get(normalizedKey);

          if (originalKey && this.variantImageMap[originalKey]) {
            return originalKey;
          }
        }

        return null;
      }

      getVariantImageEntries(variant) {
        if (!variant || !this.variantImageMap) return null;
        const key = this.buildVariantImageMapKey(variant);
        if (!key) return null;
        const entries = this.variantImageMap[key];
        return Array.isArray(entries) && entries.length ? entries : null;
      }

      cacheOriginalGalleryState() {
        // FIX: Cache both slider AND grid separately (both exist in DOM)
        if (this.originalSliderHTML !== null || this.originalGridHTML !== null) {
          return;
        }

        if (!this.productMediaGallery) {
          return;
        }

        // Detect gallery elements - BOTH can exist (CSS shows/hides)
        const galleryComponent = this.productMediaGallery.querySelector('swiper-product-gallery');
        const thumbsComponent = this.productMediaGallery.querySelector('swiper-product-thumbs');
        const gridGallery = this.productMediaGallery.querySelector('.main-product__media--grid');

        // Cache Swiper slider gallery (mobile view)
        if (galleryComponent) {
          const wrapper = galleryComponent.querySelector('.swiper-wrapper');
          if (wrapper) {
            this.originalSliderHTML = wrapper.innerHTML;
          }
        }

        // Cache Grid gallery (desktop view) - cache even if slider exists
        if (gridGallery) {
          this.originalGridHTML = gridGallery.innerHTML;
        }

        // Cache thumbnails (if using slider)
        if (thumbsComponent) {
          const wrapper = thumbsComponent.querySelector('.swiper-wrapper');
          if (wrapper) {
            this.originalThumbsHTML = wrapper.innerHTML;
          }
        }
      }

      refreshGalleryLoader() {
        if (!this.productMediaGallery) {
          this.galleryLoader = null;
          return null;
        }
        this.galleryLoader = this.productMediaGallery.querySelector('[data-media-loader]');
        return this.galleryLoader;
      }

      ensureGalleryLoader() {
        if (!this.productMediaGallery) return null;
        if (!this.galleryLoader || !this.productMediaGallery.contains(this.galleryLoader)) {
          return this.refreshGalleryLoader();
        }
        return this.galleryLoader;
      }

      lockGalleryHeight(fallbackHeight = null) {
        if (!this.productMediaGallery) return null;
        const currentHeight = Math.round(this.productMediaGallery.getBoundingClientRect().height);
        const heights = [currentHeight, this.galleryBaseHeight, fallbackHeight]
          .map(value => (typeof value === 'number' ? value : parseInt(value, 10)))
          .filter(value => Number.isFinite(value) && value > 0);
        const heightToUse = heights.length ? Math.max(...heights) : null;
        if (heightToUse) {
          this.productMediaGallery.style.minHeight = `${heightToUse}px`;
        }
        return heightToUse;
      }

      getPrimaryMediaImage(container) {
        if (!container) return null;
        return (
          container.querySelector('.swiper-slide[data-variant-image-order="0"] img') ||
          container.querySelector('.swiper-slide[data-selected] img') ||
          container.querySelector('.main-product__media--grid-item:not(.hidden) img') ||
          container.querySelector('.main-product__media--grid img') ||
          container.querySelector('img')
        );
      }

      waitForPrimaryImageLoad(container, callback, timeout = 5000) {
        const targetImage = this.getPrimaryMediaImage(container);
        if (!targetImage) {
          callback?.();
          return;
        }

        if (targetImage.complete && targetImage.naturalWidth > 0) {
          callback?.();
          return;
        }

        let timeoutId;
        const finalize = () => {
          if (timeoutId) clearTimeout(timeoutId);
          targetImage.removeEventListener('load', finalize);
          targetImage.removeEventListener('error', finalize);
          callback?.();
        };

        targetImage.addEventListener('load', finalize, { once: true });
        targetImage.addEventListener('error', finalize, { once: true });
        timeoutId = setTimeout(finalize, timeout);
      }

      measureInitialGalleryHeight() {
        if (!this.productMediaGallery) return;
        this.waitForPrimaryImageLoad(this.productMediaGallery, () => {
          this.updateGalleryBaseHeight();
        });
      }

      updateGalleryBaseHeight(height = null) {
        if (!this.productMediaGallery) return;
        const resolvedHeight = typeof height === 'number'
          ? height
          : Math.round(this.productMediaGallery.getBoundingClientRect().height);
        if (resolvedHeight > 0) {
          this.galleryBaseHeight = resolvedHeight;
        }
      }

      prepareGalleryImages(container) {
        if (!container) return;
        const images = Array.from(container.querySelectorAll('img'));
        if (!images.length) return;

        const targets = images.filter(img => img.closest('.main-product__media') && img.closest('.media'));
        if (!targets.length) return;

        targets.forEach(img => {
          img.setAttribute('data-variant-media', 'true');
        });

        this.animateGalleryImages(targets);
      }

      animateGalleryImages(targets) {
        if (!Array.isArray(targets) || !targets.length) return;

        targets.forEach(img => {
          img.classList.remove('is-loaded');

          const markLoaded = () => {
            requestAnimationFrame(() => img.classList.add('is-loaded'));
          };

          if (img.complete && img.naturalWidth > 0) {
            markLoaded();
          } else {
            const finalize = () => {
              markLoaded();
            };
            img.addEventListener('load', finalize, { once: true });
            img.addEventListener('error', finalize, { once: true });
          }
        });
      }

      showVariantImageLoadingSpinner(fallbackHeight = null) {
        if (!this.productMediaGallery) return null;
        const appliedHeight = this.lockGalleryHeight(fallbackHeight);
        const loader = this.ensureGalleryLoader();
        if (loader) {
          this.productMediaGallery.classList.add('is-loading');
          loader.removeAttribute('hidden');
          requestAnimationFrame(() => {
            loader.classList.add('active');
          });
        }
        return appliedHeight;
      }

      hideVariantImageLoadingSpinner() {
        if (!this.productMediaGallery) return;
        const loader = this.ensureGalleryLoader();
        if (loader) {
          loader.classList.remove('active');
          setTimeout(() => {
            if (!loader.classList.contains('active')) {
              loader.setAttribute('hidden', '');
            }
          }, 200);
        }
        setTimeout(() => {
          this.productMediaGallery?.style.removeProperty('min-height');
          this.productMediaGallery?.classList.remove('is-loading');
        }, 200);
      }

      renderVariantImageSlides(entries, variant, options = {}) {
        if (!this.productMediaGallery || !Array.isArray(entries) || !entries.length) {
          return;
        }

        // Limit to 3 images for made-to-order templates to match Liquid gallery behavior
        if (document.body.classList.contains('template--nv-made-to-order') && entries.length > 3) {
          entries = entries.slice(0, 3);
        }

        const { suppressSpinner = false } = options;

        if (suppressSpinner) {
          this.lockGalleryHeight();
        } else {
          this.showVariantImageLoadingSpinner();
        }

        // Detect layout type
        const galleryComponent = this.productMediaGallery.querySelector('swiper-product-gallery');
        const thumbsComponent = this.productMediaGallery.querySelector('swiper-product-thumbs');
        const gridGallery = this.productMediaGallery.querySelector('.main-product__media--grid');
        const key = this.buildVariantImageMapKey(variant);

        const buildSlideMarkup = (entry, index, isThumb = false, forGrid = false) => {
          const viewLabel = entry?.view || `Image ${index + 1}`;
          const variantTitle = variant?.title || '';
          const altText = variantTitle ? `${variantTitle} – ${viewLabel}` : viewLabel;
          const isFirst = index === 0;
          const mediaId = `variant-map-${variant?.id || 'unknown'}-${index}`;

          const loading = isThumb ? 'lazy' : isFirst ? 'eager' : 'lazy';
          const fetchPriority = !isThumb && !forGrid && isFirst ? ' fetchpriority="high"' : '';
          let selectedAttr = '';
          if (isFirst && !forGrid) {
            selectedAttr = ' data-selected';
          }

          const widths = isThumb ? [80, 120, 160, 240] : [360, 480, 600, 720, 840, 960, 1280, 1440];
          const srcset = widths.map(w => `${entry.src}?width=${w} ${w}w`).join(', ');
          const sizes = isThumb ? '96px' : '(max-width: 360px) 360px,(max-width: 480px) 480px,(max-width: 600px) 600px,(max-width: 720px) 720px,(max-width: 840px) 840px,(max-width: 960px) 960px,(max-width: 1280px) 1280px,(max-width: 1440px) 1440px, 100vw';
          const baseSrcWidth = isThumb ? 160 : 128;
          const widthAttr = isThumb ? ' width="160" height="160"' : ' width="4000" height="4000"';
          const gridStyle = forGrid ? ' style="width: 100% !important; height: auto !important; display: block !important;"' : '';
          const variantViewAttr = viewLabel ? ` data-variant-view="${viewLabel.toLowerCase()}"` : '';

          const zoomOverlay = `
            <div class="media__overlay">
              <div class="media__type--badge main-product__media--feat color-scheme-7 gradient" title="Image"></div>
              <div class="product__gallery-opener main-product__media--feat no-js-hidden color-scheme-7 gradient" data-modal-opener-id="image-${mediaId}">
                <a href="${entry.src}?width=4000" class="product__gallery-toggle motion-reduce" type="button" aria-haspopup="dialog" data-media-id="${mediaId}" data-pswp-image="" data-pswp-src="${entry.src}?width=4000" data-pswp-width="4000" data-pswp-height="4000" target="_blank">
                  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 10L4 4M4 10V4H10M14 14L20 20M20 14V20H14" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="square"></path>
                  </svg>
                  <span class="visually-hidden">Open media ${index} in modal</span>
                </a>
              </div>
            </div>`;

          const thumbOverlay = `
            <div class="media__overlay">
              <div class="media__type--badge main-product__media--feat" title="Image"></div>
            </div>`;

          const imageTag = `<img data-variant-media="true" src="${entry.src}?width=${baseSrcWidth}" alt="${altText}"${widthAttr} loading="${loading}" srcset="${srcset}" sizes="${sizes}"${fetchPriority}${variantViewAttr}${gridStyle}>`;

          if (forGrid) {
            return `<div class="main-product__media--grid-item">
              <div class="swiper-slide" data-media-id="${mediaId}" data-media-type="image" data-media-group="" data-index="${index}">
                <div class="media">
                  ${zoomOverlay}
                  ${imageTag}
                </div>
              </div>
            </div>`;
          }

          if (isThumb) {
            return `<div class="swiper-slide" data-media-id="${mediaId}" data-media-type="image" data-index="${index}"${selectedAttr}>
              <div class="media media--thumb">
                ${thumbOverlay}
                ${imageTag}
              </div>
            </div>`;
          }

          return `<div class="swiper-slide" data-variant-image-order="${index}" data-media-id="${mediaId}" data-media-type="image"${selectedAttr}>
            <div class="media">
              ${zoomOverlay}
              ${imageTag}
            </div>
          </div>`;
        };

        // Update slider, grid, and thumbnail galleries when present (CSS controls visibility)

        // Handle Swiper Slider Gallery (mobile view)
        if (galleryComponent) {
          const wrapper = galleryComponent.querySelector('.swiper-wrapper');
          if (wrapper) {
            wrapper.innerHTML = entries.map((entry, index) => buildSlideMarkup(entry, index, false, false)).join('');
          }

          const instance = galleryComponent.instance;
          if (instance) {
            if (typeof instance.updateSlides === 'function') instance.updateSlides();
            if (typeof instance.update === 'function') instance.update();
            if (typeof instance.slideTo === 'function') instance.slideTo(0, 0);
          }
        }

        // Handle Grid Gallery (desktop view) - update regardless of slider
        if (gridGallery) {
          const newHTML = entries.map((entry, index) => buildSlideMarkup(entry, index, false, true)).join('');
          gridGallery.innerHTML = newHTML;
        }

        if (thumbsComponent) {
          const thumbsWrapper = thumbsComponent.querySelector('.swiper-wrapper');
          if (thumbsWrapper) {
            thumbsWrapper.innerHTML = entries.map((entry, index) => buildSlideMarkup(entry, index, true, false)).join('');
          }

          const thumbsInstance = thumbsComponent.instance;
          if (thumbsInstance) {
            if (typeof thumbsInstance.updateSlides === 'function') thumbsInstance.updateSlides();
            if (typeof thumbsInstance.update === 'function') thumbsInstance.update();
            if (typeof thumbsInstance.slideTo === 'function') thumbsInstance.slideTo(0, 0);
          }

          if (galleryComponent?.instance?.thumbs && typeof galleryComponent.instance.thumbs.update === 'function') {
            galleryComponent.instance.thumbs.update();
          }
        }

        this.prepareGalleryImages(this.productMediaGallery);

        this.productMediaGallery.dataset.variantImageMapActive = 'true';
        this.currentVariantImageKey = key;

        let finalized = false;
        const cleanup = () => {
          if (finalized) return;
          finalized = true;
          this.productMediaGallery.classList.remove('is-loading');
          this.productMediaGallery.querySelector('.swiper--product-gallery')?.style.removeProperty('min-height');
          this.productMediaGallery.querySelector('.main-product__media--slider-wrapper')?.style.removeProperty('min-height');
          this.updateGalleryBaseHeight();
          this.hideVariantImageLoadingSpinner();
          this.hasInitializedVariantGallery = true;
        };

        this.waitForPrimaryImageLoad(this.productMediaGallery, cleanup);
        setTimeout(cleanup, 5000);
      }

      restoreOriginalGallery() {
        if (!this.productMediaGallery) {
          return;
        }

        const galleryComponent = this.productMediaGallery.querySelector('swiper-product-gallery');
        const thumbsComponent = this.productMediaGallery.querySelector('swiper-product-thumbs');
        const gridGallery = this.productMediaGallery.querySelector('.main-product__media--grid');

        if (!this.originalSliderHTML && !this.originalGridHTML) {
          return;
        }

        // FIX: Restore BOTH slider and grid separately

        // Restore Swiper Slider Gallery (mobile view)
        if (this.originalSliderHTML !== null && galleryComponent) {
          const wrapper = galleryComponent.querySelector('.swiper-wrapper');
          if (wrapper) {
            wrapper.innerHTML = this.originalSliderHTML;

            const instance = galleryComponent.instance;
            if (instance) {
              if (typeof instance.updateSlides === 'function') instance.updateSlides();
              if (typeof instance.update === 'function') instance.update();
              if (typeof instance.slideTo === 'function') instance.slideTo(0, 0);
            }
          }
        }

        // Restore Grid Gallery (desktop view)
        if (this.originalGridHTML !== null && gridGallery) {
          gridGallery.innerHTML = this.originalGridHTML;
        }

        // Restore Thumbnails
        if (this.originalThumbsHTML !== null && thumbsComponent) {
          const wrapper = thumbsComponent.querySelector('.swiper-wrapper');
          if (wrapper) {
            wrapper.innerHTML = this.originalThumbsHTML;

            const thumbsInstance = thumbsComponent.instance;
            if (thumbsInstance) {
              if (typeof thumbsInstance.updateSlides === 'function') thumbsInstance.updateSlides();
              if (typeof thumbsInstance.update === 'function') thumbsInstance.update();
              if (typeof thumbsInstance.slideTo === 'function') thumbsInstance.slideTo(0, 0);
            }

            if (galleryComponent?.instance?.thumbs && typeof galleryComponent.instance.thumbs.update === 'function') {
              galleryComponent.instance.thumbs.update();
            }
          }
        }

        this.productMediaGallery.classList.remove('is-loading');
        this.productMediaGallery.style.removeProperty('min-height');
        this.hideVariantImageLoadingSpinner();
        this.refreshGalleryLoader();
        this.updateGalleryBaseHeight();
        this.prepareGalleryImages(this.productMediaGallery);
        delete this.productMediaGallery.dataset.variantImageMapActive;
        this.currentVariantImageKey = null;
        this.hasInitializedVariantGallery = true;
      }

      applyVariantImageMap(variant) {
        const entries = this.getVariantImageEntries(variant);
        if (entries) {
          this.renderVariantImageSlides(entries, variant, {
            suppressSpinner: !this.hasInitializedVariantGallery
          });
          return true;
        }
        this.restoreOriginalGallery();
        return false;
      }

      updateVariantMedia() {
        if (this.productMediaGallery?.dataset?.variantImageMapActive === 'true') {
          return;
        }
        const select_variant_options_handle = this.querySelector(
          'product-variant-selects [data-selected-variant-options-handle]'
        )?.innerHTML.trim();

        if (!select_variant_options_handle) {
          const productAllVariants = this.productVariantSelects.querySelector('[data-all-variants]').innerHTML.trim();

          // Make sure to attach the event listener to the correct element
          this.productVariantSelects.addEventListener('click', (event) => {
            const closestVariantOptionInput = event.target.closest('.product-option__input');

            // Ensure that you are finding the correct input
            if (closestVariantOptionInput) {
              const closestVariantOptionInputValue = closestVariantOptionInput.value;

              // Parse productAllVariants and check for the selected variant
              const availableVariant = JSON.parse(productAllVariants).find(variant => {
                return variant.options.some(option => option === closestVariantOptionInputValue);
              });
            } else {
              // console.log('No variant option input found');
            }
          });
        }

        this.productMediaGallery.querySelectorAll('[data-media-group]').forEach(element => element.classList.add('hidden'));

        this.productMediaGallery.querySelectorAll(`[data-media-group="${select_variant_options_handle}"]`).forEach(element => element.classList.remove('hidden'));

        this.productMediaGallery.querySelectorAll('swiper-product-gallery').forEach(swiperSlide => {
          if (swiperSlide.instance) {
            swiperSlide.instance.update(); // Update the Swiper instance
          } else {
            swiperSlide.init(); // If the instance is not available, reinitialize
          }
        });

        this.prepareGalleryImages(this.productMediaGallery);
      }

      updateVariantOptionMedia() {
        this.productMediaGallery.querySelectorAll('[data-media-group]:not(.hidden)').forEach(element => {

        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach(productForm => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${
            variantId ? `?variant=${variantId}` : ""
          }`
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      updateSellingPlanURL(sellingPlanId, selectedVariantId) {
        if (!selectedVariantId) return;
        if (!sellingPlanId) {
          window.history.replaceState({}, "", `${window.location.href.replace(/&selling_plan=\d+/, "")}`);
        } else {
          const params = new URLSearchParams(window.location.search);
          params[params.has("variant") ? "set" : "append"](
            "variant",
            selectedVariantId
          );
          if (sellingPlanId) {
            params[params.has("selling_plan") ? "set" : "append"](
              "selling_plan",
              sellingPlanId
            );
          } else {
            params.delete("selling_plan");
          }
          window.history.replaceState({}, "", `${this.dataset.url}?${params.toString()}`);
        }
      }

      updateVariantDetails(variantId) {
        const productVariantDetails = this.querySelectorAll('.product__metafield_for_variant-block');
        if (productVariantDetails) {
          productVariantDetails.forEach((productVariantDetail) => {
            const block_id = productVariantDetail.getAttribute('data-block-id');
            const metafieldDataElement = this.querySelector(`#product__metafield_for_variant-block--${block_id}`);
            if (!metafieldDataElement) {
              return;
            }
            try {
              let productVariantDetailsData = JSON.parse(metafieldDataElement?.textContent);
              productVariantDetail.innerHTML = productVariantDetailsData?.[variantId]?.replaceAll('\n', '<br />') || '';
            } catch (error) {
              console.error('Error parsing JSON', error);
            }
          });
        }
      }

      updateVariantDiscountBadge(price, compare_at_price) {
        const discountBadge = this.querySelector(".product-card__badge--discount");
        if (!discountBadge) return;


        if (compare_at_price > price) {
          discountBadge.classList.remove("hidden");
          const percentageElement = discountBadge.querySelector(".percentage");
          // calculate discount percentage
          const discountPercentage = Math.round(
            ((compare_at_price - price) / compare_at_price) * 100
          );
          // update discount percentage
          percentageElement.textContent = discountPercentage;
        } else {
          discountBadge.classList.add("hidden");
        }
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Quantity-Rules"
        ]
          .map(id => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        if (
          this.querySelector(".main-product__media")?.hasAttribute(
            "data-desktop-grid"
          ) && window.innerWidth >= 750
        ) {
          this.scrollToImage(variantFeaturedMediaId);
        }

        const productMedia = this.querySelector(
          "swiper-product-gallery"
        );
        productMedia?.setActiveMedia(variantFeaturedMediaId);
      }

      scrollToImage(variantFeaturedMediaId) {
        this.querySelectorAll("[data-media-id]").forEach(img => {
          if (
            img.getAttribute("data-media-id") ==
            variantFeaturedMediaId
          ) {
            img.scrollIntoView({
              block: "nearest",
              inline: "nearest",
              behavior: "smooth"
            });
          }
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step
            ? parseInt(this.quantityInput.step)
            : 1
        };

        let min = data.min;
        const max =
          data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min)
          min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId =
          this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then(response => response.text())
          .then(responseText => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch(e => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner"
            ).classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label"
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step"
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(
          `#ProductModal-${this.dataset.section}`
        );
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("product-variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

// Enhanced ProductForm class with conditional ring size validation
if (!customElements.get('product-form')) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        if (!this.form) return; // No form present (e.g., sold out product)

        if (this.variantIdInput) {
          this.variantIdInput.disabled = false;
        }
        this.form.addEventListener(
          "submit",
          this.onSubmitHandler.bind(this)
        );
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton
          ? this.submitButton.querySelector("span")
          : null;

        if (document.querySelector("cart-drawer") && this.submitButton)
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      // Bands carry no bundled stone, so the internal _io_*/_pair bundle
      // metadata that the bundle-aware flow attaches is exactly what Shopify's
      // cart rejects with a 422 (a clean variant add of the same ring succeeds).
      // As a last resort -- only after every bundle-aware attempt has already
      // failed -- retry once with ONLY customer-facing properties (Ring Size,
      // engraving, etc.), dropping every "_"-prefixed internal property.
      // Resolves with the cart if the ring landed, or false otherwise. Scoped to
      // bands and runs post-failure, so it cannot regress the normal flow.
      tryBandsCleanRingAdd(variantId, quantity, ringProperties) {
        if (!document.body.classList.contains('template--bands')) return Promise.resolve(false);
        const cleanProps = {};
        Object.keys(ringProperties || {}).forEach((k) => {
          if (k.charAt(0) !== '_') cleanProps[k] = ringProperties[k];
        });
        debugLog('Add to Cart', 'Bands fallback - clean add with properties:', cleanProps);
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: parseInt(quantity) || 1, properties: cleanProps }]
          })
        })
          .then(() => fetch('/cart.js').then((r) => r.json()))
          .then((cart) => ((cart.items || []).some((i) => i.variant_id == variantId) ? cart : false))
          .catch(() => false);
      }

      handleSuccessfulCartAdd(cartData, isProductForm) {
        debugLog('Add to Cart', 'Handling successful cart add');

        // Clear saved custom field data on success
        if (isProductForm) {
          this.clearCustomFieldData();
        }

        this.error = false;
        if (this.submitButton) {
          this.submitButton.classList.remove("loading");
          this.submitButton.removeAttribute("aria-disabled");
        }
        const spinner = this.querySelector(".loading__spinner");
        if (spinner) spinner.classList.add("hidden");

        // Fetch cart sections for drawer/notification if present
        if (this.cart) {
          const sectionsToRender = this.cart.getSectionsToRender();
          const sectionsParam = sectionsToRender.map(section => section.section).join(',');

          fetch(`${window.location.pathname}?sections=${sectionsParam}`)
            .then(r => r.json())
            .then(sections => {
              debugLog('Add to Cart', 'Fetched sections for cart drawer');

              // Build response in format cart drawer expects
              const cartResponse = {
                ...cartData,
                sections: sections
              };

              if (this.cart.classList.contains("is-empty")) {
                this.cart.classList.remove("is-empty");
              }

              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: cartData.items[0]?.variant_id || '',
                cartData: cartResponse
              });

              this.cart.renderContents(cartResponse);
            })
            .catch(e => {
              if (e.name === 'AbortError') { console.warn('[Add to Cart] Sections fetch aborted'); return; }
              console.error('[Add to Cart Debug] Error fetching sections:', e);
              // Just publish the update without rendering
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: cartData.items[0]?.variant_id || '',
                cartData: cartData
              });
            });
        } else {
          // No cart drawer, just publish update
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: cartData.items[0]?.variant_id || '',
            cartData: cartData
          });
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        // Only validate ring size for product forms, not cart drawer operations
        const isProductForm = this.closest('product-info') !== null;
        const requiresRingSize = isProductForm && this.productRequiresRingSize();

        // Validate custom fields only if this is a product form submission
        if (requiresRingSize && !this.validateCustomFields()) {
          return; // Validation failed, don't proceed
        }

        if (this.submitButton && this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();
        if (this.submitButton) {
          this.submitButton.setAttribute("aria-disabled", true);
          this.submitButton.classList.add("loading");
        }
        const spinner = this.querySelector(".loading__spinner");
        if (spinner) spinner.classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        
        // Only ensure custom fields for product forms
        if (isProductForm) {
          this.ensureCustomFieldsInFormData(formData);
        }
        
        if (this.cart) {
          formData.append(
            "sections",
            this.cart
              .getSectionsToRender()
              .map(section => section.section)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // DEBUG: Log what we're sending
        debugLog('Add to Cart', 'Variant ID:', formData.get("id"));
        debugLog('Add to Cart', 'Form Data:', Array.from(formData.entries()));
        debugLog('Add to Cart', 'Request URL:', routes.cart_add_url);
        debugLog('Add to Cart', 'Request config:', config);

        // Store the variant ID we're trying to add (the ring)
        const attemptedVariantId = formData.get("id");
        const attemptedQuantity = formData.get("quantity") || 1;

        // Extract all properties from formData (for Infinite Options, ring size, etc.)
        const ringProperties = {};
        for (let [key, value] of formData.entries()) {
          if (key.startsWith('properties[') && key.endsWith(']')) {
            const propKey = key.slice(11, -1); // Extract property name

            // Include all properties, including internal Infinite Options properties (starting with underscore)
            // These are required for bundling functionality
            
            // DEBUG: Log bundle properties to understand what's being sent
            if (propKey === '_io_product_bundle_ids' || propKey === '_io_parent_order_group' || propKey === '_io_order_group') {
              debugLog('Add to Cart', 'Bundle property detected:', propKey, '=', value);
            }

            ringProperties[propKey] = value;
            if (propKey.startsWith('_')) {
              debugLog('Add to Cart', 'Including internal property:', propKey);
            }
          }
        }
        debugLog('Add to Cart', 'Ring properties to include:', ringProperties);

        // Set a safety check - wait for stone marketplace to finish, then ensure ring is added
        const cartCheckTimeout = setTimeout(() => {
          fetch('/cart.js')
            .then(r => r.json())
            .then(cartData => {
              debugLog('Add to Cart', 'Safety timeout - checking cart state:', cartData);

              // Check if our ring variant was added by stone marketplace
              const ringInCart = cartData.items?.some(item => item.variant_id == attemptedVariantId);

              if (ringInCart) {
                // Ring is already in cart, we're good!
                debugLog('Add to Cart', 'Ring found in cart, success!');
                this.handleSuccessfulCartAdd(cartData, isProductForm);
              } else if (cartData.items.length > 0) {
                // Stone marketplace added diamond but NOT the ring - add it ourselves
                debugLog('Add to Cart', 'Safety timeout - Diamond added but ring missing - adding ring now');
                debugLog('Add to Cart', 'Including properties:', ringProperties);

                // Try adding with properties first
                fetch('/cart/add.js', {
                  method: 'POST',
                  headers: {'Content-Type': 'application/json'},
                  body: JSON.stringify({
                    items: [{
                      id: attemptedVariantId,
                      quantity: parseInt(attemptedQuantity),
                      properties: ringProperties
                    }]
                  })
                })
                .then(r => r.json())
                .then(addResult => {
                  debugLog('Add to Cart', 'Safety timeout - Ring add result:', addResult);

                  // Check if it worked or got 422
                  if (addResult.status === 422 || addResult.status === 'error') {
                    debugWarn('Add to Cart', 'Safety timeout - Ring add failed with properties:', addResult.description);
                    debugWarn('Add to Cart', 'Safety timeout - Retrying without BUNDLE properties but keeping user selections.');

                    // Create a filtered properties object that excludes only bundle-related properties
                    const propertiesWithoutBundles = {};
                    for (const [key, value] of Object.entries(ringProperties)) {
                      // Exclude only the properties that enforce bundling validation
                      if (key !== '_io_product_bundle_ids' && 
                          key !== '_io_parent_order_group' && 
                          key !== '_io_order_group') {
                        propertiesWithoutBundles[key] = value;
                      } else {
                        debugLog('Add to Cart', 'Safety timeout - Excluding bundle property from retry:', key);
                      }
                    }

                    debugLog('Add to Cart', 'Safety timeout - Retrying with filtered properties:', propertiesWithoutBundles);

                    // Try again WITHOUT bundle properties but WITH user selections
                    return fetch('/cart/add.js', {
                      method: 'POST',
                      headers: {'Content-Type': 'application/json'},
                      body: JSON.stringify({
                        items: [{
                          id: attemptedVariantId,
                          quantity: parseInt(attemptedQuantity),
                          properties: propertiesWithoutBundles
                        }]
                      })
                    }).then(r => r.json());
                  }

                  return addResult;
                })
                .then(finalResult => {
                  debugLog('Add to Cart', 'Safety timeout - Final result:', finalResult);

                  // Fetch updated cart
                  return fetch('/cart.js').then(r => r.json());
                })
                .then(updatedCart => {
                  debugLog('Add to Cart', 'Safety timeout - Cart after adding ring:', updatedCart);

                  // Check if ring was actually added
                  const ringNowInCart = updatedCart.items?.some(item => item.variant_id == attemptedVariantId);

                  if (ringNowInCart) {
                    this.handleSuccessfulCartAdd(updatedCart, isProductForm);
                  } else {
                    // Last-resort clean add for bands (no bundled stone).
                    this.tryBandsCleanRingAdd(attemptedVariantId, attemptedQuantity, ringProperties).then(cart => {
                      if (cart) { this.handleSuccessfulCartAdd(cart, isProductForm); return; }
                      console.error('[Add to Cart Debug] Safety timeout - Ring still not in cart');
                      this.handleErrorMessage('Unable to add ring to cart. Please try again.');
                      this.error = true;
                      if (this.submitButton) this.submitButton.classList.remove("loading");
                      const spinner1 = this.querySelector(".loading__spinner");
                      if (spinner1) spinner1.classList.add("hidden");
                    });
                  }
                })
                .catch(e => {
                  if (e.name === 'AbortError') { console.warn('[Add to Cart] Safety timeout ring-add aborted'); return; }
                  console.error('[Add to Cart Debug] Safety timeout - Error adding ring:', e);
                  this.handleErrorMessage('Unable to add ring to cart. Please try again.');
                  this.error = true;
                  if (this.submitButton) this.submitButton.classList.remove("loading");
                  const spinner2 = this.querySelector(".loading__spinner");
                  if (spinner2) spinner2.classList.add("hidden");
                });
              }
            })
            .catch(e => { if (e.name === 'AbortError') return; console.error('[Add to Cart Debug] Safety check error:', e); });
        }, 2000); // Wait 2 seconds for stone marketplace to finish

        fetch(`${routes.cart_add_url}`, config)
          .then(response => response.json())
          .then(response => {
            clearTimeout(cartCheckTimeout); // Cancel safety check if we got a response
            debugLog('Add to Cart', 'Response received:', response);

            // Check if we have a cart with items (stone marketplace may have added items despite error)
            if (response.items && response.items.length > 0) {
              debugLog('Add to Cart', 'Items successfully added to cart:', response.items);
              // Treat as success even if there's a status error
              response.status = null;
            }

            if (response.status) {
              // DEBUG: Log the full error response
              debugError('Add to Cart Error', 'Full response:', response);
              debugError('Add to Cart Error', 'Status:', response.status);
              debugError('Add to Cart Error', 'Description:', response.description);
              debugError('Add to Cart Error', 'Message:', response.message);

              // IMPORTANT: Check if cart actually has items before showing error
              // Stone marketplace might add items successfully despite returning error status
              fetch('/cart.js')
                .then(r => r.json())
                .then(cartData => {
                  debugLog('Add to Cart', 'Checking cart state:', cartData);

                  // Check if our RING variant was actually added
                  const ringInCart = cartData.items?.some(item => item.variant_id == attemptedVariantId);

                  if (ringInCart) {
                    // Ring is in cart, we're good!
                    debugLog('Add to Cart', 'Ring found in cart, treating as success');
                    this.handleSuccessfulCartAdd(cartData, isProductForm);
                  } else if (cartData.items && cartData.items.length > 0) {
                    // Stone marketplace added diamond but NOT the ring - add it ourselves
                    debugLog('Add to Cart', 'Diamond added but ring missing - adding ring now');
                    debugLog('Add to Cart', 'Attempting ring add with properties:', ringProperties);

                    // Try adding with properties first
                    fetch('/cart/add.js', {
                      method: 'POST',
                      headers: {'Content-Type': 'application/json'},
                      body: JSON.stringify({
                        items: [{
                          id: attemptedVariantId,
                          quantity: parseInt(attemptedQuantity),
                          properties: ringProperties
                        }]
                      })
                    })
                    .then(r => r.json())
                    .then(addResult => {
                      debugLog('Add to Cart', 'Ring add result:', addResult);

                      // Check if it worked or got 422
                      if (addResult.status === 422 || addResult.status === 'error') {
                        debugWarn('Add to Cart', 'Ring add failed with properties:', addResult.description);
                        debugWarn('Add to Cart', 'Retrying without BUNDLE properties but keeping user selections.');

                        // Create a filtered properties object that excludes only bundle-related properties
                        const propertiesWithoutBundles = {};
                        for (const [key, value] of Object.entries(ringProperties)) {
                          // Exclude only the properties that enforce bundling validation
                          if (key !== '_io_product_bundle_ids' && 
                              key !== '_io_parent_order_group' && 
                              key !== '_io_order_group') {
                            propertiesWithoutBundles[key] = value;
                          } else {
                            debugLog('Add to Cart', 'Excluding bundle property from retry:', key);
                          }
                        }

                        debugLog('Add to Cart', 'Retrying with filtered properties:', propertiesWithoutBundles);

                        // Try again WITHOUT bundle properties but WITH user selections
                        return fetch('/cart/add.js', {
                          method: 'POST',
                          headers: {'Content-Type': 'application/json'},
                          body: JSON.stringify({
                            items: [{
                              id: attemptedVariantId,
                              quantity: parseInt(attemptedQuantity),
                              properties: propertiesWithoutBundles
                            }]
                          })
                        }).then(r => r.json());
                      }

                      return addResult;
                    })
                    .then(finalResult => {
                      debugLog('Add to Cart', 'Final ring add result:', finalResult);

                      // Now fetch the updated cart
                      return fetch('/cart.js').then(r => r.json());
                    })
                    .then(updatedCart => {
                      debugLog('Add to Cart', 'Cart after adding ring:', updatedCart);

                      // Check if ring was actually added
                      const ringNowInCart = updatedCart.items?.some(item => item.variant_id == attemptedVariantId);

                      if (ringNowInCart) {
                        debugLog('Add to Cart', 'Ring successfully added!');
                        this.handleSuccessfulCartAdd(updatedCart, isProductForm);
                      } else {
                        // Last-resort clean add for bands (no bundled stone).
                        this.tryBandsCleanRingAdd(attemptedVariantId, attemptedQuantity, ringProperties).then(cart => {
                          if (cart) { this.handleSuccessfulCartAdd(cart, isProductForm); return; }
                          console.error('[Add to Cart Debug] Ring still not in cart after attempts');
                          this.handleErrorMessage('Unable to add ring to cart. Please contact support.');
                          this.error = true;
                          if (this.submitButton) this.submitButton.classList.remove("loading");
                          const spinner3 = this.querySelector(".loading__spinner");
                          if (spinner3) spinner3.classList.add("hidden");
                        });
                      }
                    })
                    .catch(e => {
                      if (e.name === 'AbortError') { console.warn('[Add to Cart] Ring-add aborted'); return; }
                      console.error('[Add to Cart Debug] Error adding ring:', e);
                      // Show error since ring couldn't be added
                      this.handleErrorMessage('Unable to add ring to cart. Please try again.');
                      this.error = true;
                      if (this.submitButton) this.submitButton.classList.remove("loading");
                      const spinner4 = this.querySelector(".loading__spinner");
                      if (spinner4) spinner4.classList.add("hidden");
                    });
                  } else {
                    // Cart is actually empty, show error
                    publish(PUB_SUB_EVENTS.cartError, {
                      source: "product-form",
                      productVariantId: formData.get("id"),
                      errors: response.errors || response.description,
                      message: response.message
                    });
                    this.handleErrorMessage(response.description);

                    const soldOutMessage = this.submitButton
                      ? this.submitButton.querySelector(".sold-out-message")
                      : null;
                    if (soldOutMessage) {
                      this.submitButton.setAttribute("aria-disabled", true);
                      if (this.submitButtonText) this.submitButtonText.classList.add("hidden");
                      soldOutMessage.classList.remove("hidden");
                    }
                    this.error = true;
                  }
                })
                .catch(e => {
                  if (e.name === 'AbortError') { console.warn('[Add to Cart] Cart check aborted'); return; }
                  console.error('[Add to Cart Debug] Error checking cart:', e);
                  // Show error message
                  this.handleErrorMessage(response.description);
                  this.error = true;
                });

              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // Success - clear saved custom field data
            if (isProductForm) {
              this.clearCustomFieldData();
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response
              });
            this.error = false;
            this.cart.renderContents(response);
          })
          .catch(e => {
            clearTimeout(cartCheckTimeout);
            if (e.name === 'AbortError') {
              console.warn('[Add to Cart] Request aborted (user navigated away or page unloaded)');
              return;
            }
            console.error(e);
          })
          .finally(() => {
            clearTimeout(cartCheckTimeout);
            if (this.submitButton) {
              this.submitButton.classList.remove("loading");
              if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            }
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            const spinner5 = this.querySelector(".loading__spinner");
            if (spinner5) spinner5.classList.add("hidden");
          });
      }

      productRequiresRingSize() {
        // FIX: Check if THIS specific form needs a ring size
        // Only check within this form, not globally on the page
        return this.form.querySelector('#RingSize') !== null;
      }

      validateCustomFields() {
        // Validate ring size
        const ringSizeSelect = this.form.querySelector('#RingSize');
        if (ringSizeSelect && !ringSizeSelect.value) {
          this.showRingSizeError();
          return false;
        }
        
        this.clearRingSizeError();
        return true;
      }

      ensureCustomFieldsInFormData(formData) {
        // Ensure ring size is included
        const ringSizeSelect = this.form.querySelector('#RingSize');
        if (ringSizeSelect && ringSizeSelect.value) {
          // Remove any existing ring size entries to avoid duplicates
          formData.delete('properties[Ring Size]');
          formData.append('properties[Ring Size]', ringSizeSelect.value);
        }

        // Ensure custom engraving is included
        const customEngraving = this.form.querySelector('#CustomEngraving');
        if (customEngraving && customEngraving.value) {
          // The field should already have the correct name, but ensure it
          customEngraving.name = 'properties[Custom Engraving]';
        }

        // Ensure all Infinite Options fields are properly included
        // This handles Size and any other custom fields from Infinite Options app
        const infiniteOptionsContainer = document.getElementById('infiniteoptions-container');
        if (infiniteOptionsContainer) {
          // Find all input fields within the Infinite Options container
          const infiniteOptionsFields = infiniteOptionsContainer.querySelectorAll('input[name^="properties"], select[name^="properties"], textarea[name^="properties"]');
          
          infiniteOptionsFields.forEach(field => {
            // Handle different input types
            let shouldInclude = false;
            let fieldValue = '';
            
            if (field.type === 'checkbox' || field.type === 'radio') {
              // Only include if checked
              if (field.checked) {
                shouldInclude = true;
                fieldValue = field.value;
              }
            } else {
              // For text, select, textarea - include if has value
              if (field.value) {
                shouldInclude = true;
                fieldValue = field.value;
              }
            }
            
            if (shouldInclude && field.name) {
              // Extract the property name from the field name (e.g., properties[Size])
              const propertyMatch = field.name.match(/properties\[(.*?)\]/);
              if (propertyMatch && propertyMatch[1]) {
                const propertyName = propertyMatch[1];
                // Remove any existing entries to avoid duplicates
                formData.delete(field.name);
                // Add the current value
                formData.append(field.name, fieldValue);
                // console.log(`Infinite Options field ensured: ${propertyName} = ${fieldValue}`);
              }
            }
          });
        }
      }

      showRingSizeError() {
        const ringSizeError = document.getElementById('ring-size-error');
        const ringSizeSelect = document.getElementById('RingSize');
        
        if (ringSizeError) {
          ringSizeError.style.display = 'block';
        }
        
        if (ringSizeSelect) {
          ringSizeSelect.style.borderColor = '#ff3860';
          ringSizeSelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      clearRingSizeError() {
        const ringSizeError = document.getElementById('ring-size-error');
        const ringSizeSelect = document.getElementById('RingSize');
        
        if (ringSizeError) {
          ringSizeError.style.display = 'none';
        }
        
        if (ringSizeSelect) {
          ringSizeSelect.style.borderColor = '#333';
        }
      }

      clearCustomFieldData() {
        sessionStorage.removeItem('ringSize');
        sessionStorage.removeItem('engravingText');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute(
          "hidden",
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          if (this.submitButtonText) {
            this.submitButtonText.textContent = this.hasAttribute(
              "data-has-selling-plan"
            )
              ? window.variantStrings.addSubscriptionToCart
              : window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form ? this.form.querySelector("[name=id]") : null;
      }
    }
  );
}

// Enhanced form submission handler with context awareness
(function() {
  'use strict';
  
  // Function to check if the request is from a product page
  function isProductPageRequest(source) {
    // Check various indicators that this is a product page request
    if (source instanceof FormData) {
      // Check if the form has ring size field
      const formElement = document.querySelector('form[action*="/cart/add"]:not(.cart-drawer__form)');
      if (formElement && formElement.querySelector('#RingSize')) {
        return true;
      }
    }
    
    // Check if we're in a product-info context
    return document.querySelector('product-info') !== null && 
           document.querySelector('#RingSize') !== null;
  }
  
  // Function to ensure ring size is always included in form data (only for product pages)
  function ensureRingSizeInFormData(formData, isProductPage) {
    if (!isProductPage) return false;
    
    const ringSizeSelect = document.getElementById('RingSize');
    
    if (ringSizeSelect && ringSizeSelect.value) {
      // Remove any existing ring size entries to avoid duplicates
      formData.delete('properties[Ring Size]');
      // Add the current ring size value
      formData.append('properties[Ring Size]', ringSizeSelect.value);
      // console.log('Ring size added to form data:', ringSizeSelect.value);
      return true;
    }
    return false;
  }
  
  // Function to ensure all Infinite Options fields are included
  function ensureInfiniteOptionsInFormData(formData, isProductPage) {
    if (!isProductPage) return;
    
    const infiniteOptionsContainer = document.getElementById('infiniteoptions-container');
    if (!infiniteOptionsContainer) return;
    
    // Find all input fields within the Infinite Options container
    const fields = infiniteOptionsContainer.querySelectorAll('input[name^="properties"], select[name^="properties"], textarea[name^="properties"]');
    
    fields.forEach(field => {
      // Handle different input types
      let shouldInclude = false;
      let fieldValue = '';
      
      if (field.type === 'checkbox' || field.type === 'radio') {
        // Only include if checked
        if (field.checked) {
          shouldInclude = true;
          fieldValue = field.value;
        }
      } else {
        // For text, select, textarea - include if has value
        if (field.value) {
          shouldInclude = true;
          fieldValue = field.value;
        }
      }
      
      if (shouldInclude && field.name) {
        // Extract the property name from the field name
        const propertyMatch = field.name.match(/properties\[(.*?)\]/);
        if (propertyMatch && propertyMatch[1]) {
          const propertyName = propertyMatch[1];
          // Remove any existing entries to avoid duplicates
          formData.delete(field.name);
          // Add the current value
          formData.append(field.name, fieldValue);
          // console.log(`Infinite Options field ensured in fetch: ${propertyName} = ${fieldValue}`);
        }
      }
    });
  }
  
  // Intercept fetch requests with context awareness
  const originalFetch = window.fetch;
  window.fetch = function(url, options) {
     // Check if this is a cart add request
    if (
      url &&
      typeof url === 'string' &&
      (url.includes('/cart/add') || url.includes('cart/add.js'))
    ) { 
      // Determine if this is from a product page
      const isFromProductPage = isProductPageRequest(options?.body);
      
      // Only validate ring size for product page requests
      if (isFromProductPage) {
        // console.log('Intercepting product page cart/add request');
        
        // Handle FormData
        if (options && options.body instanceof FormData) {
          const ringSizeSelect = document.getElementById('RingSize');
          
          // Only validate if ring size field exists and is empty
          if (ringSizeSelect && !ringSizeSelect.value) {
            // Instead of blocking, we'll let the ProductForm handle validation
            // console.log('Ring size validation will be handled by ProductForm');
          } else {
            ensureRingSizeInFormData(options.body, true);
          }
          
          // Always ensure Infinite Options fields are included
          ensureInfiniteOptionsInFormData(options.body, true);
        }
        
        // Handle JSON requests
        if (options && options.body && typeof options.body === 'string') {
          try {
            const data = JSON.parse(options.body);
            
            // Ensure properties object exists
            if (!data.properties) {
              data.properties = {};
            }
            
            // Add ring size if present
            const ringSizeSelect = document.getElementById('RingSize');
            if (ringSizeSelect && ringSizeSelect.value) {
              data.properties['Ring Size'] = ringSizeSelect.value;
              // console.log('Ring size added to JSON request:', ringSizeSelect.value);
            }
            
            // Add all Infinite Options fields
            const infiniteOptionsContainer = document.getElementById('infiniteoptions-container');
            if (infiniteOptionsContainer) {
              const fields = infiniteOptionsContainer.querySelectorAll('input[name^="properties"], select[name^="properties"], textarea[name^="properties"]');
              
              fields.forEach(field => {
                // Handle different input types
                let shouldInclude = false;
                let fieldValue = '';
                
                if (field.type === 'checkbox' || field.type === 'radio') {
                  // Only include if checked
                  if (field.checked) {
                    shouldInclude = true;
                    fieldValue = field.value;
                  }
                } else {
                  // For text, select, textarea - include if has value
                  if (field.value) {
                    shouldInclude = true;
                    fieldValue = field.value;
                  }
                }
                
                if (shouldInclude && field.name) {
                  const propertyMatch = field.name.match(/properties\[(.*?)\]/);
                  if (propertyMatch && propertyMatch[1]) {
                    const propertyName = propertyMatch[1];
                    data.properties[propertyName] = fieldValue;
                    // console.log(`Infinite Options field added to JSON: ${propertyName} = ${fieldValue}`);
                  }
                }
              });
            }
            
            options.body = JSON.stringify(data);
          } catch (e) {
            console.error('Error parsing JSON in fetch intercept:', e);
          }
        }
      } else {
        // console.log('Cart operation from cart drawer - skipping ring size validation');
      }
    }
    
    // Continue with original fetch
    return originalFetch.apply(this, arguments);
  };
  
  // Form submission handler with context awareness
  function handleFormSubmission(event) {
    const form = event.target;

    // Check if this is a product form (not cart drawer form)
    if (form.action && form.action.includes('/cart/add')) {
      // Skip validation for cart drawer forms
      if (form.closest('.cart-drawer') || form.classList.contains('cart-drawer__form')) {
        // console.log('Cart drawer form submission - skipping ring size validation');
        return;
      }

      // console.log('Product form submission detected');

      // FIX: Check if ring size field exists within THIS specific form
      const ringSizeSelect = form.querySelector('#RingSize');

      // Only validate if THIS form has a ring size field
      if (ringSizeSelect) {
        // Validate ring size is selected
        if (!ringSizeSelect.value) {
          event.preventDefault();

          // Show error
          const ringSizeError = form.querySelector('#ring-size-error') || document.getElementById('ring-size-error');
          if (ringSizeError) {
            ringSizeError.style.display = 'block';
          }
          if (ringSizeSelect) {
            ringSizeSelect.style.borderColor = '#ff3860';
            ringSizeSelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }

          // console.log('Form submission blocked: Ring size not selected');
          return false;
        }
      } else {
        // console.log('This form does not require ring size - allowing submission');
      }
    }
  }
  
  // Enhanced initialization
  function initRingSizeHandler() {
    // console.log('Initializing context-aware ring size handler');
    
    // Add form submit listeners
    document.addEventListener('submit', handleFormSubmission, true);
    
    // Listen for add to cart button clicks with context checking
    document.addEventListener('click', function(event) {
      const target = event.target;
      const button = target.closest('[name="add"], .btn-product-add, .product-form__submit, button[type="submit"], input[type="submit"]');

      if (!button) {
        return;
      }

      const form = button.closest('form[data-product-form], form[data-type="add-to-cart-form"], form[action*="/cart/add"]');

      if (!form) {
        return;
      }

      // Skip if this is in the cart drawer
      if (button.closest('.cart-drawer') || button.closest('[data-cart-drawer]')) {
        // console.log('Cart drawer button clicked - no ring size validation needed');
        return;
      }

      // Skip if this is a checkout button
      const buttonText = button.textContent?.toLowerCase() || '';
      const buttonValue = typeof button.value === 'string' ? button.value.toLowerCase() : '';
      if (button.name === 'checkout' || buttonText.includes('checkout') || buttonValue.includes('checkout')) {
        // console.log('Checkout button clicked - no ring size validation needed');
        return;
      }

      // console.log('Product add to cart button clicked');

      // FIX: Check if ring size field exists within THIS specific form (reuse form from line 2064)
      const ringSizeSelect = form ? form.querySelector('#RingSize') : null;

      // Only validate if THIS form has a ring size field and not in cart drawer
      if (ringSizeSelect && !button.closest('.cart-drawer')) {
        if (!ringSizeSelect.value) {
          event.preventDefault();
          event.stopPropagation();

          // Show error
          const ringSizeError = form.querySelector('#ring-size-error') || document.getElementById('ring-size-error');
          if (ringSizeError) {
            ringSizeError.style.display = 'block';
          }
          if (ringSizeSelect) {
            ringSizeSelect.style.borderColor = '#ff3860';
            ringSizeSelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }

          // console.log('Add to cart blocked: Ring size not selected');
          return false;
        }
      } else if (form && !ringSizeSelect) {
        // console.log('This product does not require ring size - allowing add to cart');
      }
    });
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRingSizeHandler);
  } else {
    initRingSizeHandler();
  }
  
})();
